import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Playlist } from "store/playlist/playlist.model";
import type { Track } from "store/track/track.model";

interface SnackbarState {
  isOpen: boolean;
  message: string;
  severity: "success" | "info" | "warning" | "error";
}

interface ProfileMenu {
  isOpen: boolean;
}

interface TrackMenu {
  isOpen: boolean;
  track: Track | null;
  isOnPlayerScreen: boolean;
  isDeletable: boolean;
  playlist: Playlist | null;
}

interface RegionMenu {
  isOpen: boolean;
}

interface AddToPlaylistMenu {
  isOpen: boolean;
  track: Track | null;
}

interface CreatePlaylistMenu {
  isOpen: boolean;
}

interface PlaylistMenu {
  isOpen: boolean;
  playlist: Playlist | null;
  isEditable: boolean;
}

interface PlaylistRemoveConfirmationMenu {
  isOpen: boolean;
  playlist: Playlist | null;
}

interface EditPlaylistMenu {
  isOpen: boolean;
  playlist: Playlist | null;
}

interface EditUserFullNameMenu {
  isOpen: boolean;
}

interface EditUserGenderMenu {
  isOpen: boolean;
}

interface EditUserUserNameMenu {
  isOpen: boolean;
}

interface EditUserEmailMenu {
  isOpen: boolean;
}

interface EditUserPhoneMenu {
  isOpen: boolean;
}

interface EditUserPasswordMenu {
  isOpen: boolean;
}

interface LogoutConfirmationMenu {
  isOpen: boolean;
}

interface DeleteAccountConfirmationMenu {
  isOpen: boolean;
}

interface UserMenu {
  isOpen: boolean;
  userId: string | null;
  shouldShowProfileButton: boolean;
}

interface ReportUserMenu {
  isOpen: boolean;
  userId: string | null;
}

interface ReportUserDoneMenu {
  isOpen: boolean;
}

interface ChatMenu {
  isOpen: boolean;
  userId: string | null;
  chatId: string | null;
}

interface DownloadApplicationMenu {
  isOpen: boolean;
  isCloseable: boolean;
}

interface AppState {
  snackbar: SnackbarState;
  scrollOffset: number;
  profileMenu: ProfileMenu;
  trackMenu: TrackMenu;
  regionMenu: RegionMenu;
  addToPlaylistMenu: AddToPlaylistMenu;
  createPlaylistMenu: CreatePlaylistMenu;
  playlistMenu: PlaylistMenu;
  playlistRemoveConfirmationMenu: PlaylistRemoveConfirmationMenu;
  editPlaylistMenu: EditPlaylistMenu;
  editUserFullNameMenu: EditUserFullNameMenu;
  editUserGenderMenu: EditUserGenderMenu;
  editUserUserNameMenu: EditUserUserNameMenu;
  editUserEmailMenu: EditUserEmailMenu;
  editUserPhoneMenu: EditUserPhoneMenu;
  editUserPasswordMenu: EditUserPasswordMenu;
  logoutConfirmationMenu: LogoutConfirmationMenu;
  deleteAccountConfirmationMenu: DeleteAccountConfirmationMenu;
  userMenu: UserMenu;
  reportUserMenu: ReportUserMenu;
  reportUserDoneMenu: ReportUserDoneMenu;
  chatMenu: ChatMenu;
  downloadApplicationMenu: DownloadApplicationMenu;
}

const initialState: AppState = {
  snackbar: { isOpen: false, message: "", severity: "info" },
  scrollOffset: 0,
  profileMenu: { isOpen: false },
  trackMenu: {
    isOpen: false,
    track: null,
    isOnPlayerScreen: false,
    isDeletable: false,
    playlist: null,
  },
  regionMenu: { isOpen: false },
  addToPlaylistMenu: { isOpen: false, track: null },
  createPlaylistMenu: { isOpen: false },
  playlistMenu: { isOpen: false, playlist: null, isEditable: false },
  playlistRemoveConfirmationMenu: { isOpen: false, playlist: null },
  editPlaylistMenu: { isOpen: false, playlist: null },
  editUserFullNameMenu: { isOpen: false },
  editUserGenderMenu: { isOpen: false },
  editUserUserNameMenu: { isOpen: false },
  editUserEmailMenu: { isOpen: false },
  editUserPhoneMenu: { isOpen: false },
  editUserPasswordMenu: { isOpen: false },
  logoutConfirmationMenu: { isOpen: false },
  deleteAccountConfirmationMenu: { isOpen: false },
  userMenu: { isOpen: false, userId: null, shouldShowProfileButton: false },
  reportUserMenu: { isOpen: false, userId: null },
  reportUserDoneMenu: { isOpen: false },
  chatMenu: { isOpen: false, userId: null, chatId: null },
  downloadApplicationMenu: { isOpen: false, isCloseable: true },
};

export const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    setSnackbar: (state, action: PayloadAction<SnackbarState>) => {
      state.snackbar = action.payload;
    },
    hideSnackbar: (state, action: PayloadAction<void>) => {
      state.snackbar.isOpen = false;
    },
    setScrollOffset: (state, action: PayloadAction<number>) => {
      state.scrollOffset = action.payload;
    },
    setProfileMenu: (state, action: PayloadAction<ProfileMenu>) => {
      state.profileMenu = action.payload;
    },
    setTrackMenu: (state, action: PayloadAction<TrackMenu>) => {
      state.trackMenu = action.payload;
    },
    setRegionMenu: (state, action: PayloadAction<RegionMenu>) => {
      state.regionMenu = action.payload;
    },
    setAddToPlaylistMenu: (state, action: PayloadAction<AddToPlaylistMenu>) => {
      state.addToPlaylistMenu = action.payload;
    },
    setCreatePlaylistMenu: (
      state,
      action: PayloadAction<CreatePlaylistMenu>
    ) => {
      state.createPlaylistMenu = action.payload;
    },
    setPlaylistMenu: (state, action: PayloadAction<PlaylistMenu>) => {
      state.playlistMenu = action.payload;
    },
    setPlaylistRemoveConfirmationMenu: (
      state,
      action: PayloadAction<PlaylistRemoveConfirmationMenu>
    ) => {
      state.playlistRemoveConfirmationMenu = action.payload;
    },
    setEditPlaylistMenu: (state, action: PayloadAction<EditPlaylistMenu>) => {
      state.editPlaylistMenu = action.payload;
    },
    setEditUserFullNameMenu: (
      state,
      action: PayloadAction<EditUserFullNameMenu>
    ) => {
      state.editUserFullNameMenu = action.payload;
    },
    setEditUserGenderMenu: (
        state,
        action: PayloadAction<EditUserFullNameMenu>
    ) => {
      state.editUserGenderMenu = action.payload;
    },
    setEditUserUserNameMenu: (
      state,
      action: PayloadAction<EditUserUserNameMenu>
    ) => {
      state.editUserUserNameMenu = action.payload;
    },
    setEditUserEmailMenu: (state, action: PayloadAction<EditUserEmailMenu>) => {
      state.editUserEmailMenu = action.payload;
    },
    setEditUserPhoneMenu: (state, action: PayloadAction<EditUserPhoneMenu>) => {
      state.editUserPhoneMenu = action.payload;
    },
    setEditUserPasswordMenu: (
      state,
      action: PayloadAction<EditUserPasswordMenu>
    ) => {
      state.editUserPasswordMenu = action.payload;
    },
    setLogoutConfirmationMenu: (
      state,
      action: PayloadAction<LogoutConfirmationMenu>
    ) => {
      state.logoutConfirmationMenu = action.payload;
    },
    setDeleteAccountConfirmationMenu: (
      state,
      action: PayloadAction<DeleteAccountConfirmationMenu>
    ) => {
      state.deleteAccountConfirmationMenu = action.payload;
    },
    setUserMenu: (state, action: PayloadAction<UserMenu>) => {
      state.userMenu = action.payload;
    },
    setReportUserMenu: (state, action: PayloadAction<ReportUserMenu>) => {
      state.reportUserMenu = action.payload;
    },
    setReportUserDoneMenu: (
      state,
      action: PayloadAction<ReportUserDoneMenu>
    ) => {
      state.reportUserDoneMenu = action.payload;
    },
    setChatMenu: (state, action: PayloadAction<ChatMenu>) => {
      state.chatMenu = action.payload;
    },
    setDownloadApplicationMenu: (state, action: PayloadAction<DownloadApplicationMenu>) => {
      state.downloadApplicationMenu = action.payload;
    },
  },
});

export const { reducer: appReducer, actions: appActions } = appSlice;
